import { disableBodyScroll, enableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock'
import { AnimatePresence, motion } from 'framer-motion'
import React, { useEffect, useRef, useState } from 'react'
import FocusLock from 'react-focus-lock'
import { useIsomorphicLayoutEffect, useMedia } from 'react-use'
import styled from 'styled-components'
import { media } from 'utils/media'
import { SearchEmpty } from './SearchEmpty'
import { SearchModalMobileHeader } from './SearchModalMobileHeader'
import { SearchNoResults } from './SearchNoResults'
import { SearchResults } from './SearchResults'
import { useAlgoliaSearchContext } from './controllers/AlgoliaSearchContext'

const Modal = styled(motion.div)`
  background-color: white;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  min-height: 100vh;
  padding: 0 16px;
  position: absolute;
  width: 70%;
  z-index: 10;

  ${media.phoneDown} {
    bottom: 0;
    left: 0;
    position: fixed;
    right: 0;
    top: 0;
    width: 100%;
    z-index: 2000;
    overflow: hidden;
  }
`

const Container = styled(motion.div)`
  margin-top: 8px;
  display: flex;
  flex-direction: column;

  ${media.phoneDown} {
    justify-content: 'flex-start';
    min-height: 100vh;
  }
`

const ResultsContainer = styled(motion.div)`
  overflow-y: auto;

  ${media.phoneDown} {
    padding-bottom: 50vh;
  }
`

export const SearchModalMobile = () => {
  const contentRef = useRef<HTMLDivElement | null>(null)
  const [contentRefVisible, setContentRefVisible] = useState(false)
  const { isModalOpen, isEmpty, isResultsReturned, isNoResults } = useAlgoliaSearchContext()
  const isMobile = useMedia('(max-width: 576px)')

  useEffect(() => {
    if (contentRefVisible && contentRef.current && isMobile) {
      if (open) {
        disableBodyScroll(contentRef.current)
      } else {
        enableBodyScroll(contentRef.current)
      }
    }

    return () => {
      clearAllBodyScrollLocks()
    }
  }, [isModalOpen, contentRefVisible, isMobile])

  // Scroll to top after the modal opens, otherwise the autofocusing keyboard will push the top of the modal out of view
  useIsomorphicLayoutEffect(() => {
    if (isModalOpen) {
      setTimeout(() => window.scrollTo({ top: 0, behavior: 'smooth' }), 150)
    }
  }, [isModalOpen])

  return (
    <>
      <AnimatePresence>
        {isModalOpen && (
          <FocusLock disabled={false}>
            <Modal initial={{ opacity: 1 }} animate={{ opacity: 1 }} exit={{ opacity: 0, transition: { ease: 'easeOut' } }}>
              <Container
                initial={{ opacity: 0.5, translateY: -12 }}
                animate={{ opacity: 1, translateY: 0 }}
                exit={{ opacity: 0, translateY: 0 }}
                transition={{ opacity: { duration: 0.2 }, translateY: { duration: 0.2 } }}>
                <SearchModalMobileHeader />
                <ResultsContainer
                  layout
                  ref={(el) => {
                    contentRef.current = el
                    setContentRefVisible(!!el)
                  }}>
                  {isEmpty && <SearchEmpty />}
                  {isResultsReturned && <SearchResults />}
                  {isNoResults && <SearchNoResults />}
                </ResultsContainer>
              </Container>
            </Modal>
          </FocusLock>
        )}
      </AnimatePresence>
    </>
  )
}
